/**
 * SupplierSpendBoard — admin-only view of per-supplier spend, revenue and
 * markup across orders. Helps negotiate volume rebates and spot cost creep.
 * Customer-invisible: never link to it from the public storefront.
 *
 * Reads from:
 *   GET /api/admin/orders/supplier-spend?days=N      → rollup cards
 *   GET /api/admin/orders/supplier-spend/{name}/lines?days=N → drill-down
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  Building2,
  Package as PackageIcon,
  DollarSign,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
const fmtInt = (n) => (n == null ? "—" : Number(n).toLocaleString());

const WINDOWS = [
  { id: 30, label: "Last 30 days" },
  { id: 90, label: "Last 90 days" },
  { id: 180, label: "Last 6 months" },
  { id: 365, label: "Last 12 months" },
  { id: 730, label: "All time (2 yr)" },
];

export default function SupplierSpendBoard({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState(null); // currently drilled-down supplier
  const [lines, setLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/api/admin/orders/supplier-spend?days=${days}`, { headers })
      .then((r) => setData(r.data))
      .catch(() => setData({ suppliers: [] }))
      .finally(() => setLoading(false));
  }, [days, headers]);

  const openDrillDown = async (s) => {
    setFocus(s);
    setLinesLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/admin/orders/supplier-spend/${encodeURIComponent(s.supplier)}/lines?days=${days}`,
        { headers },
      );
      setLines(r.data.lines || []);
    } catch {
      setLines([]);
    } finally {
      setLinesLoading(false);
    }
  };

  if (focus) {
    return (
      <SupplierDrillDown
        supplier={focus}
        days={days}
        lines={lines}
        loading={linesLoading}
        onBack={() => setFocus(null)}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="supplier-spend-board">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-emerald-600" />
            Supplier Spend & Margin
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Admin-only. Aggregated from every order line's <code className="font-mono">internal_cost</code> × <code className="font-mono">unit_cost</code> × qty.
          </p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-44" data-testid="supplier-spend-window">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOWS.map((w) => (
              <SelectItem key={w.id} value={String(w.id)}>{w.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <KpiCard icon={Building2} label="Suppliers" value={data?.supplier_count ?? "—"} subtle="active in window" />
        <KpiCard icon={DollarSign} label="You paid" value={fmt$(data?.grand_total_spend)} subtle="internal cost · admin-only" colorClass="text-amber-700" />
        <KpiCard icon={DollarSign} label="Customers paid" value={fmt$(data?.grand_total_revenue)} subtle="sell-side revenue" colorClass="text-slate-900" />
        <KpiCard icon={TrendingUp} label="Avg markup" value={fmtPct(data?.grand_avg_markup_pct)} subtle="weighted across all SKUs" colorClass="text-emerald-700" />
      </div>

      {/* Supplier list */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>By Supplier (largest spend first)</span>
            {loading && <span className="text-xs text-slate-400 font-normal">Loading…</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!loading && (data?.suppliers || []).length === 0 ? (
            <p className="text-sm text-slate-500 italic py-6 text-center">
              No supplier data in the last {days} days. Add a <code className="font-mono">Supplier</code> value to your order line items to populate this rollup.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 px-2">Supplier</th>
                    <th className="text-right py-2 px-2">Units</th>
                    <th className="text-right py-2 px-2">Orders</th>
                    <th className="text-right py-2 px-2">SKUs</th>
                    <th className="text-right py-2 px-2 text-amber-700">🔒 You paid</th>
                    <th className="text-right py-2 px-2">Customer paid</th>
                    <th className="text-right py-2 px-2 text-amber-700">🔒 Avg internal</th>
                    <th className="text-right py-2 px-2">Avg unit</th>
                    <th className="text-right py-2 px-2 text-emerald-700">Markup</th>
                    <th className="text-left py-2 px-2 pl-3">Categories</th>
                    <th className="text-right py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.suppliers || []).map((s) => {
                    const m = s.avg_markup_pct;
                    const mcolor = m == null
                      ? "text-slate-400"
                      : m >= 30
                        ? "text-emerald-700"
                        : m >= 10
                          ? "text-amber-700"
                          : "text-rose-700";
                    return (
                      <tr
                        key={s.supplier}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => openDrillDown(s)}
                        data-testid={`supplier-spend-row-${s.supplier.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <td className="py-2.5 px-2 font-semibold text-slate-900">{s.supplier}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{fmtInt(s.unit_count)}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{fmtInt(s.order_count)}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{fmtInt(s.sku_count)}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums font-semibold text-amber-700">{fmt$(s.internal_spend)}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums font-semibold">{fmt$(s.customer_revenue)}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums text-amber-700">{fmt$(s.avg_internal_cost)}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{fmt$(s.avg_unit_price)}</td>
                        <td className={`py-2.5 px-2 text-right tabular-nums font-bold ${mcolor}`}>{fmtPct(m)}</td>
                        <td className="py-2.5 px-3 text-xs text-slate-500">{(s.categories || []).join(", ") || "—"}</td>
                        <td className="py-2.5 px-2 text-right text-slate-400">
                          <ChevronRight className="w-4 h-4 inline" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, subtle, colorClass = "text-slate-900" }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
            <p className={`text-2xl font-extrabold mt-1 ${colorClass}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">{subtle}</p>
          </div>
          <Icon className="w-5 h-5 text-slate-300" />
        </div>
      </CardContent>
    </Card>
  );
}

function SupplierDrillDown({ supplier, days, lines, loading, onBack }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Button
            size="sm"
            variant="outline"
            onClick={onBack}
            className="gap-1 mb-2"
            data-testid="supplier-drill-back"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </Button>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-emerald-600" />
            {supplier.supplier}
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Line-by-line spend over the last {days} days · {supplier.unit_count.toLocaleString()} units across {supplier.order_count} orders
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Window markup</p>
          <p className="text-3xl font-extrabold text-emerald-700">{fmtPct(supplier.avg_markup_pct)}</p>
          <p className="text-[11px] text-slate-400">{fmt$(supplier.internal_spend)} → {fmt$(supplier.customer_revenue)}</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="text-sm text-slate-500 italic py-6 text-center">Loading line items…</p>
          ) : lines.length === 0 ? (
            <p className="text-sm text-slate-500 italic py-6 text-center">No line items in this window.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="text-left py-2 px-3">Date</th>
                    <th className="text-left py-2 px-3">Order</th>
                    <th className="text-left py-2 px-3">Hospital</th>
                    <th className="text-left py-2 px-3">SKU</th>
                    <th className="text-left py-2 px-3">Item</th>
                    <th className="text-right py-2 px-3">Qty</th>
                    <th className="text-right py-2 px-3 text-amber-700">🔒 Internal</th>
                    <th className="text-right py-2 px-3">Unit</th>
                    <th className="text-right py-2 px-3 text-emerald-700">Markup</th>
                    <th className="text-right py-2 px-3">Line $</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="py-2 px-3 text-xs text-slate-500">{(l.created_at || "").slice(0, 10)}</td>
                      <td className="py-2 px-3 font-mono text-xs">{l.order_id}</td>
                      <td className="py-2 px-3 text-xs">{l.hospital_name}</td>
                      <td className="py-2 px-3 font-mono text-xs text-slate-500">{l.sku || "—"}</td>
                      <td className="py-2 px-3 max-w-[200px] truncate" title={l.name}>{l.name}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmtInt(l.quantity)}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-amber-700">{fmt$(l.internal_cost)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmt$(l.unit_cost)}</td>
                      <td className="py-2 px-3 text-right tabular-nums font-bold text-emerald-700">{fmtPct(l.markup_pct)}</td>
                      <td className="py-2 px-3 text-right tabular-nums font-bold">{fmt$(l.customer_line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
